export interface LineItem {
  quantity: number;
  component: string;
  description?: string;
}

export interface PaymentPeriod {
  /** ISO date (yyyy-mm-dd) the payment period begins. */
  start_date: string;
  /** Dollar amount due for this period. */
  amount: number;
}

/** A reference agency selected for Section VI of the proposal. */
export interface ProposalReference {
  agency_name: string;
  contact_name: string;
  contact_phone: string;
}

/** A Diamond Club reference agency from the backing database. */
export interface DiamondClubMember {
  id: string;
  agency_name: string;
  contact_name: string;
  contact_phone: string;
  /** "Y" pre-checks the agency in the wizard; "N" leaves it unchecked. */
  default: "Y" | "N";
}

export interface ProposalModel {
  agency_name: string;
  agency_address: string;
  /** Backwards-compat: full contact name; new records use split fields below. */
  agency_contact_name: string;
  agency_contact_title: string;
  agency_contact_first_name: string;
  agency_contact_last_name: string;
  agency_state: string;
  service_types: string[];
  term_months: number;
  salesperson_name: string;
  salesperson_title: string;
  salesperson_phone: string;
  salesperson_email: string;
  line_items: LineItem[];
  total_investment_amount: number;
  /** "TBD" (or empty) for to-be-determined, otherwise an ISO yyyy-mm-dd date. */
  target_start_date: string;
  /** When true, payments are spread equally across the term (default). */
  equal_payments: boolean;
  /** Custom payment schedule (used only when equal_payments is false). */
  payment_periods: PaymentPeriod[];
  /** Diamond Club reference agencies featured in Section VI (min 3). */
  selected_references: ProposalReference[];
  proposal_date: string;
  proposal_id: string;
}

export const CONTACT_TITLE_OPTIONS = [
  "Chief", "Sheriff", "Captain", "Lieutenant", "Sergeant",
  "Detective", "Officer", "Director", "Commissioner",
  "Mr.", "Ms.", "Mrs.", "Dr.",
] as const;

export interface CompositionSection {
  source: string;
  prompt_version: string;
  user_instruction: string | null;
  text_snapshot: string;
  inputs_hash: string;
  generated_at: string;
}

export interface Proposal {
  id: string;
  author_user_id: string;
  author_email: string;
  status: "draft" | "submitted" | "approved" | "changes_requested";
  template_id: string;
  template_version: number;
  model_revision: number;
  model: ProposalModel;
  composition: Record<string, CompositionSection>;
  submission: { submitted_at: string | null; model_snapshot_hash: string | null };
  approval: {
    approved_by: string | null;
    approved_at: string | null;
    rejection_reason: string | null;
  };
  artifacts: {
    draft_pdf_blob: string | null;
    draft_docx_blob: string | null;
    final_pdf_blob: string | null;
  };
  ai_review: { report: string | null; completed_at: string | null };
  created_at: string;
  updated_at: string;
}

export const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA",
  "HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
  "DC",
] as const;

export const SERVICE_TYPE_OPTIONS = [
  { value: "BWC", label: "Body-Worn Camera (BWC)" },
  { value: "ICV", label: "In-Car Video (ICV)" },
  { value: "FC", label: "Fixed Camera (FC)" },
] as const;

export const STATUS_LABELS: Record<Proposal["status"], string> = {
  draft: "Draft",
  submitted: "Submitted",
  approved: "Approved",
  changes_requested: "Changes Requested",
};

export const STATUS_COLORS: Record<Proposal["status"], "informative" | "warning" | "success" | "danger"> = {
  draft: "informative",
  submitted: "warning",
  approved: "success",
  changes_requested: "danger",
};
